// START IMPORTS
import { Stack, Text, Grid, Box, styled } from '@ltvco/refresh-lib/theme';
import { titleize } from '@ltvco/refresh-lib/utils';
import {
  useSearchPerson,
  ResultFilter,
  ResultCard,
  PersonSearchLoading,
  PersonSearchNoResults,
  Record,
  SearchPersonProps,
} from '@ltvco/refresh-lib/v1';
import {
  PersonResultsPagination,
  usePersonResultsPagination,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
// END IMPORTS

// START STYLED_COMPONENTS
const StickyContainer = styled(Stack)(({ theme }) => ({
  position: 'fixed',
  top: 60,
  width: '100%',
  zIndex: 2,

  [theme.breakpoints.up('md')]: {
    position: 'sticky',
    top: theme.spacing(10),
    zIndex: 1,
    marginBottom: theme.spacing(3),
    height: 'unset',
    overflowY: 'unset',
  },
}));
// END STYLED_COMPONENTS

export function SearchPersonResults() {
  // START HOOKS_AND_QUERIES
  const [searchParams] = useSearchParams();
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  const { currentPage } = usePersonResultsPagination();
  // END HOOKS_AND_QUERIES

  // START EFFECTS
  useEffect(() => {
    document.title = 'Search - NumberGuru';
  }, []);
  // END EFFECTS

  // START DERIVED_DATA_AND_UTILS
  let searchData = {
    fName: searchParams.get('fname') || '',
    mName: searchParams.get('mn'),
    lName: searchParams.get('ln') || '',
    state:
      searchParams.get('state') === 'All' ? 'All' : searchParams.get('state'),
    city: searchParams.get('city'),
    age: searchParams.get('age'),
    showPartialAddresses: isInPartialAddressesVariation,
    page: currentPage?.toString() || '',
  };

  const SearchPersonQuery: SearchPersonProps = {
    searchPersonBy: 'name',
    options: {
      personSearchParams: searchData,
      // TODO: Once the OptOut implementation starts, improve this
      isOptout: false,
    },
  };

  let { data: searchPersonResult = [], isFetched } =
    useSearchPerson(SearchPersonQuery);
  searchPersonResult = searchPersonResult as Record[];
  const nextPages = (searchPersonResult as Record[] & { nextPages?: number })
    .nextPages;
  const exactMatchCount = searchPersonResult.filter(
    (person: Record) => person.nameMatched
  ).length;

  const getFullName = () => {
    const { fName, mName, lName } = searchData;

    const nameData = [fName, mName, lName].filter(Boolean);
    const fullName = nameData.join(' ');

    return titleize(fullName.toLowerCase());
  };

  // TODO: implement or add pluralize for general usage
  const resultsTitle = exactMatchCount
    ? `${exactMatchCount} result${exactMatchCount > 1 ? 's' : ''}`
    : 'No exact match';
  // END DERIVED_DATA_AND_UTILS

  // START RENDER

  return (
    <>
      {!isFetched ? (
        <PersonSearchLoading />
      ) : (
        <Stack sx={{ minHeight: 800, position: 'relative' }}>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <StickyContainer>
                <ResultFilter personInfo={searchData} />
              </StickyContainer>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 9,
              }}
              sx={{ mt: { xs: 8.75, md: 0 } }}
            >
              {searchPersonResult.length ? (
                <>
                  <Grid size={12}>
                    <Box
                      mb={{ xs: 2, md: 2 }}
                      ml={{ xs: 2, md: 0 }}
                      nonce={undefined}
                    >
                      <Text variant="h3" display={'inline'}>
                        {resultsTitle}
                      </Text>
                      <Text variant="h4" fontWeight={400} display={'inline'}>
                        {' '}
                        for{' '}
                      </Text>
                      <Text variant="h3" display={'inline'}>
                        {getFullName()}
                      </Text>
                    </Box>
                  </Grid>
                  {searchPersonResult.map((person: Record, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <ResultCard {...person} isPersonSearchCard />
                      </React.Fragment>
                    );
                  })}
                  <PersonResultsPagination nextPages={nextPages} />
                </>
              ) : (
                <>
                  <PersonSearchNoResults />
                  {currentPage > 1 && (
                    <PersonResultsPagination nextPages={nextPages} />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Stack>
      )}
    </>
  );
  // END RENDER
}
